import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaCrown, FaLockOpen } from 'react-icons/fa';
import { createPortal } from 'react-dom';
import { API_BASE, apiFetch } from '../lib/api';
import { toast } from 'react-toastify';
import { FaBan, FaPencil } from 'react-icons/fa6';
import { useAuth } from '../context/AuthContext';

type Role = 'admin' | 'agent';

type TeamUser = {
    id: number;
    name: string;
    code?: string;
    role: Role;
    is_banned: number;
    depots: string[];
    modified_at: string;
    modified_by_name?: string | null;
};

type DepotMultiSelectProps = {
    selected: string[];
    onChange: (next: string[]) => void;
    searchDepots: (q: string) => Promise<string[]>;
    disabled?: boolean;
    className?: string;
};

function DepotMultiSelect({ selected, onChange, searchDepots, disabled = false, className = '' }: DepotMultiSelectProps) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [options, setOptions] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [menuStyle, setMenuStyle] = useState<{ top: number; left: number; width: number; listMaxHeight: number }>({
        top: 0,
        left: 0,
        width: 320,
        listMaxHeight: 256,
    });
    const rootRef = useRef<HTMLDivElement | null>(null);
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const triggerRef = useRef<HTMLButtonElement | null>(null);

    useEffect(() => {
        const onDocClick = (e: MouseEvent) => {
            const target = e.target as Node;
            const insideRoot = rootRef.current?.contains(target);
            const insideDropdown = dropdownRef.current?.contains(target);
            if (!insideRoot && !insideDropdown) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, []);

    useEffect(() => {
        if (!open) return;

        const recalc = () => {
            const rect = triggerRef.current?.getBoundingClientRect();
            if (!rect) return;
            const viewportW = window.innerWidth;
            const viewportH = window.innerHeight;
            const margin = 8;
            const gap = 8;
            const menuEstimatedHeight = 320;
            const spaceBelow = viewportH - rect.bottom - margin;
            const spaceAbove = rect.top - margin;
            const openUpward = spaceBelow < 240 && spaceAbove > spaceBelow;
            const width = Math.max(260, Math.min(rect.width, 420, viewportW - margin * 2));
            const left = Math.min(Math.max(margin, rect.left), Math.max(margin, viewportW - width - margin));
            const top = openUpward
                ? Math.max(margin, rect.top - menuEstimatedHeight - gap)
                : Math.min(viewportH - menuEstimatedHeight - margin, rect.bottom + gap);
            const availableHeight = openUpward ? spaceAbove - gap : spaceBelow - gap;
            const listMaxHeight = Math.max(140, Math.min(256, availableHeight - 64));

            setMenuStyle({
                top,
                left,
                width,
                listMaxHeight,
            });
        };

        recalc();
        window.addEventListener('resize', recalc);
        window.addEventListener('scroll', recalc, true);
        return () => {
            window.removeEventListener('resize', recalc);
            window.removeEventListener('scroll', recalc, true);
        };
    }, [open]);

    useEffect(() => {
        if (!open) return;
        let active = true;
        setLoading(true);
        const t = setTimeout(() => {
            searchDepots(query)
                .then((items) => {
                    if (!active) return;
                    setOptions(items);
                })
                .catch(() => {
                    if (!active) return;
                    setOptions([]);
                })
                .finally(() => {
                    if (!active) return;
                    setLoading(false);
                });
        }, 200);
        return () => {
            active = false;
            clearTimeout(t);
        };
    }, [open, query, searchDepots]);

    const mergedOptions = useMemo(
        () => Array.from(new Set([...selected, ...options])),
        [selected, options]
    );

    const toggleDepot = (depot: string) => {
        if (selected.includes(depot)) {
            onChange(selected.filter((x) => x !== depot));
        } else {
            onChange([...selected, depot]);
        }
    };

    return (
        <div ref={rootRef} className={`relative ${className}`}>
            <button
                ref={triggerRef}
                type="button"
                disabled={disabled}
                onClick={() => setOpen((x) => !x)}
                className="w-full text-left border border-slate-200/80 bg-white/80 rounded-lg p-2.5 disabled:bg-slate-100"
            >
                {selected.length > 0 ? `${selected.length} depot(s) selected` : 'Select depots'}
            </button>

            {selected.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                    {selected.map((depot) => (
                        <span key={depot} className="inline-flex items-center bg-sky-100 text-sky-700 rounded-full px-2 py-1 text-xs">
                            {depot}
                            {!disabled && (
                                <button
                                    type="button"
                                    className="ml-2 text-sky-700 cursor-pointer"
                                    onClick={() => toggleDepot(depot)}
                                >
                                    x
                                </button>
                            )}
                        </span>
                    ))}
                </div>
            )}

            {open && !disabled && typeof document !== 'undefined' && createPortal(
                <div
                    ref={dropdownRef}
                    className="fixed z-[9999] rounded-xl border border-slate-200 bg-white/95 backdrop-blur-md shadow-xl p-2"
                    style={{ top: menuStyle.top, left: menuStyle.left, width: menuStyle.width }}
                >
                    <input
                        className="w-full border border-slate-200 rounded-lg p-2 mb-2 outline-none focus:ring-2 focus:ring-sky-200"
                        placeholder="Search depots..."
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                    />
                    <div className="overflow-auto" style={{ maxHeight: menuStyle.listMaxHeight }}>
                        {loading ? (
                            <div className="p-2 text-sm text-slate-500">Searching...</div>
                        ) : mergedOptions.length === 0 ? (
                            <div className="p-2 text-sm text-slate-500">No depots found</div>
                        ) : (
                            mergedOptions.map((depot) => (
                                <label key={depot} className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={selected.includes(depot)}
                                        onChange={() => toggleDepot(depot)}
                                    />
                                    <span className="text-sm text-slate-700">{depot}</span>
                                </label>
                            ))
                        )}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}

export default function TeamMembers() {
    const { user: authUser } = useAuth();
    const [users, setUsers] = useState<TeamUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');

    const [name, setName] = useState('');
    const [code, setCode] = useState('');
    const [role, setRole] = useState<Role>('agent');
    const [selectedDepots, setSelectedDepots] = useState<string[]>([]);

    const [creating, setCreating] = useState(false);
    const [updatingRoleId, setUpdatingRoleId] = useState<number | null>(null);
    const [updatingDepotsId, setUpdatingDepotsId] = useState<number | null>(null);
    const [updatingCodeId, setUpdatingCodeId] = useState<number | null>(null);
    const [updatingBanId, setUpdatingBanId] = useState<number | null>(null);
    const [hoveredCodeId, setHoveredCodeId] = useState<number | null>(null);
    const [draftDepots, setDraftDepots] = useState<Record<number, string[]>>({});
    const [draftCodes, setDraftCodes] = useState<Record<number, string>>({});
    const depotsCacheRef = useRef<Record<string, string[]>>({});

    const showMessage = (text: string, error = false) => {
        if (error) {
            toast.error(text);
        } else {
            toast.success(text);
        }
    };

    const searchDepots = async (q: string) => {
        const key = q.trim().toLowerCase();
        if (depotsCacheRef.current[key]) return depotsCacheRef.current[key];

        const res = await apiFetch(`${API_BASE}/api/depots?q=${encodeURIComponent(q)}`);
        const data = await res.json().catch(() => ({ depots: [] }));
        const depots = Array.isArray(data?.depots) ? data.depots : [];
        depotsCacheRef.current[key] = depots;
        return depots;
    };

    const loadUsers = async () => {
        setLoading(true);
        try {
            const res = await apiFetch(`${API_BASE}/api/users`);
            const data = await res.json();
            if (!res.ok) {
                showMessage(data?.message ?? 'Failed to load users', true);
                return;
            }
            const rows: TeamUser[] = (data.users ?? []).map((u: any) => ({
                ...u,
                depots: Array.isArray(u.depots) ? u.depots : [],
                is_banned: Number(u.is_banned ?? 0),
            }));
            setUsers(rows);
            const initialDrafts: Record<number, string[]> = {};
            const initialCodes: Record<number, string> = {};
            rows.forEach((u) => {
                initialDrafts[u.id] = [...u.depots];
                // The server only supplies the signed-in user's existing code.
                // Other rows intentionally start blank so an admin can set a
                // replacement without learning the previous value.
                initialCodes[u.id] = typeof u.code === 'string' ? u.code : '';
            });
            setDraftDepots(initialDrafts);
            setDraftCodes(initialCodes);
        } catch {
            showMessage('Failed to load users', true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadUsers();
    }, []);

    // toast handles lifecycle; no inline banner state needed

    const handleCreate = async () => {
        const trimmedName = name.trim();
        const trimmedCode = code.trim();
        if (!trimmedName || !trimmedCode) {
            showMessage('Name and code are required', true);
            return;
        }
        if (role === 'agent' && selectedDepots.length === 0) {
            showMessage('Select at least one depot for agent', true);
            return;
        }

        setCreating(true);
        try {
            const res = await apiFetch(`${API_BASE}/api/users`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: trimmedName,
                    code: trimmedCode,
                    role,
                    depots: selectedDepots,
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                showMessage(data?.message ?? 'Failed to create user', true);
                return;
            }

            setName('');
            setCode('');
            setRole('agent');
            setSelectedDepots([]);
            showMessage('User created');
            await loadUsers();
        } catch {
            showMessage('Failed to create user', true);
        } finally {
            setCreating(false);
        }
    };

    const handleRoleChange = async (id: number, nextRole: Role) => {
        setUpdatingRoleId(id);
        try {
            const res = await apiFetch(`${API_BASE}/api/users/${id}/role`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ role: nextRole }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                showMessage(data?.message ?? 'Failed to update role', true);
                return;
            }

            setUsers((prev) => prev.map((u) => (
                u.id === id
                    ? { ...u, role: nextRole, depots: nextRole === 'admin' ? [] : u.depots }
                    : u
            )));
            setDraftDepots((prev) => ({
                ...prev,
                [id]: nextRole === 'admin' ? [] : (prev[id] ?? []),
            }));
            showMessage('Role updated');
        } catch {
            showMessage('Failed to update role', true);
        } finally {
            setUpdatingRoleId(null);
        }
    };

    const handleUpdateDepots = async (userId: number, nextDepots: string[]) => {
        if (nextDepots.length === 0) {
            showMessage('Select at least one depot for agent', true);
            return;
        }

        const prevDepots = draftDepots[userId] ?? [];
        setDraftDepots((prev) => ({ ...prev, [userId]: nextDepots }));
        setUpdatingDepotsId(userId);
        try {
            const res = await apiFetch(`${API_BASE}/api/users/${userId}/depots`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ depots: nextDepots }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setDraftDepots((prev) => ({ ...prev, [userId]: prevDepots }));
                showMessage(data?.message ?? 'Failed to update depots', true);
                return;
            }

            setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, depots: nextDepots } : u)));
            showMessage('Depots updated');
        } catch {
            setDraftDepots((prev) => ({ ...prev, [userId]: prevDepots }));
            showMessage('Failed to update depots', true);
        } finally {
            setUpdatingDepotsId(null);
        }
    };

    const handleSaveCode = async (userId: number) => {
        const codeValue = String(draftCodes[userId] ?? '').trim();
        if (!codeValue) {
            showMessage('Login code is required', true);
            return;
        }

        setUpdatingCodeId(userId);
        try {
            const res = await apiFetch(`${API_BASE}/api/users/${userId}/code`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ code: codeValue }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                showMessage(data?.message ?? 'Failed to update login code', true);
                return;
            }

            // Do not retain a newly set code in the browser. The server does
            // not return other members' codes, and changing a code invalidates
            // that member's session anyway.
            setDraftCodes((prev) => ({ ...prev, [userId]: '' }));
            setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, code: undefined } : u)));
            showMessage('Login code updated');
        } catch {
            showMessage('Failed to update login code', true);
        } finally {
            setUpdatingCodeId(null);
        }
    };

    const handleToggleBan = async (userId: number, nextBanned: boolean) => {
        setUpdatingBanId(userId);
        try {
            const res = await apiFetch(`${API_BASE}/api/users/${userId}/ban`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ banned: nextBanned }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                showMessage(data?.message ?? 'Failed to update ban status', true);
                return;
            }

            setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, is_banned: nextBanned ? 1 : 0 } : u)));
            showMessage(nextBanned ? 'User banned' : 'User unbanned');
        } catch {
            showMessage('Failed to update ban status', true);
        } finally {
            setUpdatingBanId(null);
        }
    };

    const filteredUsers = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return users;
        return users.filter((u) => u.name.toLowerCase().includes(q));
    }, [users, searchQuery]);

    return (
        <div className="min-h-screen p-4 md:p-6 bg-[radial-gradient(circle_at_top_left,_#e0f2fe,_#f8fafc_40%,_#eef2ff_100%)]">
            <div className="mb-6 rounded-2xl border border-white/40 bg-white/60 backdrop-blur-md shadow-[0_10px_30px_rgba(15,23,42,0.08)] p-5 md:p-6">
                <h1 className="text-2xl font-semibold tracking-tight text-slate-800 mb-1">Team Members</h1>
                <p className="text-sm text-slate-600">Manage users, roles, and depot access.</p>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-start">
                <div className="xl:col-span-8 rounded-2xl border border-white/40 bg-white/65 backdrop-blur-md shadow-[0_10px_30px_rgba(15,23,42,0.08)] overflow-auto">
                    <div className="p-4 border-b border-slate-200/70 bg-white/70">
                        <input
                            className="w-full border border-slate-200/80 bg-white/90 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-sky-200"
                            placeholder="Search members by name"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    {loading ? (
                        <div className="p-4 text-slate-600">Loading users...</div>
                    ) : (
                        <table className="min-w-full text-sm">
                            <thead>
                                <tr className="bg-white/70 text-left text-slate-700">
                                    <th className="p-3">Name</th>
                                    <th className="p-3">Login Code</th>
                                    <th className="p-3">Role</th>
                                    <th className="p-3">Depots</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsers.map((u) => {
                                    const rowDepots = draftDepots[u.id] ?? [];
                                    const banDisabled = updatingBanId === u.id;
                                    const draftCode = draftCodes[u.id] ?? '';
                                    const isCodeDirty = draftCode !== (u.code ?? '');
                                    const canViewCode = u.id === authUser?.id && typeof u.code === 'string';
                                    return (
                                        <tr key={u.id} className="border-t border-slate-200/70 hover:bg-white/40 transition align-top">
                                            <td className="p-3 font-medium text-slate-800">
                                                <span className="inline-flex items-center gap-2 capitalize">
                                                    {u.name}
                                                    {u.role === 'admin' && (
                                                        <span
                                                            className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-700 px-2 py-0.5 text-[11px] font-semibold"
                                                            title="Admin user"
                                                        >
                                                            <FaCrown className="text-[10px]" />
                                                        </span>
                                                    )}
                                                    {u.is_banned === 1 && (
                                                        <span
                                                            className="inline-flex items-center gap-1 rounded-full bg-red-100 text-red-700 px-2 py-0.5 text-[11px] font-semibold"
                                                            title="Banned user"
                                                        >
                                                            Banned
                                                        </span>
                                                    )}
                                                </span>
                                            </td>
                                            <td className="p-3 text-slate-700">
                                                <div className="flex flex-row gap-4 items-center">
                                                    <div
                                                        className="relative w-full"
                                                        onMouseEnter={() => {
                                                            if (canViewCode) setHoveredCodeId(u.id);
                                                        }}
                                                        onMouseLeave={() => setHoveredCodeId(null)}
                                                    >
                                                        <input
                                                            className="w-full border border-slate-200/80 bg-white/90 rounded-lg px-2.5 py-1.5 pr-9"
                                                            type={canViewCode && hoveredCodeId === u.id ? 'text' : 'password'}
                                                            value={draftCode}
                                                            placeholder={canViewCode ? undefined : 'Set new login code'}
                                                            onChange={(e) => setDraftCodes((prev) => ({ ...prev, [u.id]: e.target.value }))}
                                                            disabled={updatingCodeId === u.id}
                                                        />
                                                        <FaPencil
                                                            className={`absolute right-2 top-1/2 -translate-y-1/2 text-lg outline-none ${isCodeDirty ? 'text-blue-400 hover:text-blue-600 cursor-pointer' : 'text-slate-300'}`}
                                                            data-tooltip-id="my-tooltip"
                                                            data-tooltip-content={isCodeDirty ? 'Change login code' : 'No changes'}
                                                            onClick={() => {
                                                                if (isCodeDirty) handleSaveCode(u.id)
                                                            }}
                                                        />
                                                    </div>
                                                    {u.is_banned === 1 ? (
                                                        <FaLockOpen
                                                            className={`text-green-400 hover:text-green-600 cursor-pointer text-2xl ${banDisabled ? 'opacity-50 pointer-events-none' : ''}`}
                                                            data-tooltip-id="my-tooltip"
                                                            data-tooltip-content="Unban user"
                                                            onClick={() => {
                                                                if (!banDisabled) handleToggleBan(u.id, false);
                                                            }}
                                                        />
                                                    ) : (
                                                        <FaBan
                                                            className={`text-red-400 hover:text-red-600 cursor-pointer text-2xl ${banDisabled ? 'opacity-50 pointer-events-none' : ''}`}
                                                            data-tooltip-id="my-tooltip"
                                                            data-tooltip-content="Ban user"
                                                            onClick={() => {
                                                                if (!banDisabled) handleToggleBan(u.id, true);
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            </td>
                                            <td className="p-3">
                                                <select
                                                    className="border border-slate-200/80 bg-white/90 rounded-lg px-2.5 py-1.5"
                                                    value={u.role}
                                                    onChange={(e) => handleRoleChange(u.id, e.target.value as Role)}
                                                    disabled={updatingRoleId === u.id}
                                                >
                                                    <option value="agent" className='capitalize'>Agent</option>
                                                    <option value="admin" className='capitalize'>Admin</option>
                                                </select>
                                            </td>
                                            <td className="p-3 min-w-[280px]">
                                                {u.role === 'agent' ? (
                                                    <div>
                                                        <DepotMultiSelect
                                                            selected={rowDepots}
                                                            onChange={(next) => handleUpdateDepots(u.id, next)}
                                                            searchDepots={searchDepots}
                                                            disabled={updatingDepotsId === u.id}
                                                            className="max-w-xs"
                                                        />
                                                    </div>
                                                ) : (
                                                    <span className="text-slate-500 text-xs">N/A for admin</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                                {filteredUsers.length === 0 && (
                                    <tr>
                                        <td className="p-3 text-slate-500" colSpan={4}>
                                            {users.length === 0 ? 'No users found' : 'No matching users'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>

                <div className="xl:col-span-4 rounded-2xl border border-white/40 bg-white/65 backdrop-blur-md shadow-[0_10px_30px_rgba(15,23,42,0.08)] p-5">
                    <h2 className="text-lg font-semibold text-slate-800 mb-4">Create User</h2>
                    <div className="space-y-3">
                        <div>
                            <label className="block text-xs text-slate-600 mb-1">Name</label>
                            <input
                                className="w-full border border-slate-200/80 bg-white/80 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-sky-200"
                                placeholder="Enter name of user"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-slate-600 mb-1">Login Code</label>
                            <input
                                className="w-full border border-slate-200/80 bg-white/80 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-sky-200"
                                placeholder="Enter login code for user"
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-slate-600 mb-1">Role</label>
                            <select
                                className="w-full border border-slate-200/80 bg-white/80 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-sky-200"
                                value={role}
                                onChange={(e) => {
                                    const nextRole = e.target.value as Role;
                                    setRole(nextRole);
                                    if (nextRole === 'admin') setSelectedDepots([]);
                                }}
                            >
                                <option value="agent" className='capitalize'>Agent</option>
                                <option value="admin" className='capitalize'>Admin</option>
                            </select>
                        </div>
                        {role === 'agent' && (
                            <div>
                                <label className="block text-xs text-slate-600 mb-1">Depots</label>
                                <DepotMultiSelect
                                    selected={selectedDepots}
                                    onChange={setSelectedDepots}
                                    searchDepots={searchDepots}
                                    className="max-w-sm"
                                />
                            </div>
                        )}
                        <button
                            className="w-full bg-slate-900 text-white rounded-lg px-4 py-2.5 disabled:bg-slate-400 hover:bg-slate-800 transition cursor-pointer"
                            onClick={handleCreate}
                            disabled={creating}
                        >
                            {creating ? 'Creating...' : 'Create User'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
